/* eslint-disable @typescript-eslint/no-explicit-any */
import { StateCreator } from 'zustand';
import { createAppStore } from '@marriott/mi-store-utils';
import { produce } from 'immer';
import { MixedInventoryStore, MixedInventoryBookingState, MixedInventoryBookingActions } from './interface';
import { initialMixedInventoryState } from './defaultState';
import { SearchCriteriaState, SelectedRoom, MixedInventoryScenario } from './types';

/**
 * Mixed Inventory Booking Store
 *
 * Manages state for multi-room booking flow with session storage persistence
 *
 * @pattern Zustand with Immer middleware for immutable updates
 * @persistence Session storage (cart expires per session)
 * @devTools Redux DevTools enabled in development
 */

const mixedInventoryStoreCreator: StateCreator<MixedInventoryStore> = (set, get) => {
  return {
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // INITIAL STATE
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    ...initialMixedInventoryState,

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // INITIALIZATION ACTIONS
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

    /**
     * Initialize booking with search criteria from RLM page
     * Called when user enters Mixed Inventory flow
     *
     * @flow
     * 1. Set search criteria (property, dates, room counts)
     * 2. Reset room progression to Room 1
     * 3. Clear any existing cart/errors
     * 4. Update metadata timestamp and sessionId
     */
    initializeBooking: (searchCriteria: SearchCriteriaState) => {
      set(
        produce((draft: MixedInventoryBookingState) => {
          draft.searchCriteria = searchCriteria;
          draft.roomProgression = {
            currentRoomIndex: 1,
            roomsAdded: 0,
            roomsRemaining: searchCriteria.totalRooms,
          };
          draft.cartState = {
            cartId: null,
            expirationTime: null,
            rooms: [],
            cartExtensionCount: 0,
          };
          draft.errors = {
            cartCreationError: null,
            inventoryShortage: false,
            datesMismatch: false,
          };
          draft.metadata = {
            ...draft.metadata,
            timestamp: new Date().toISOString(),
            sessionId: `mi-booking-${Date.now()}`,
          };
        })
      );
    },

    /**
     * Reset entire booking state to initial defaults
     * Called on abandonment, expiry, or successful checkout
     */
    resetBooking: () => {
      set(
        produce((draft: MixedInventoryBookingState) => {
          Object.assign(draft, initialMixedInventoryState);
          draft.metadata.timestamp = new Date().toISOString();
        })
      );
    },

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // SEARCH CRITERIA MANAGEMENT
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

    /**
     * Update search criteria (dates, property, room counts)
     * Triggers date mismatch validation
     */
    updateSearchCriteria: (criteria: Partial<SearchCriteriaState>) => {
      set(
        produce((draft: MixedInventoryBookingState) => {
          draft.searchCriteria = { ...draft.searchCriteria, ...criteria };
          draft.metadata.timestamp = new Date().toISOString();
        })
      );
    },

    /**
     * Update total rooms count
     * Recalculates roomsRemaining
     */
    updateTotalRooms: (totalRooms: number) => {
      set(
        produce((draft: MixedInventoryBookingState) => {
          draft.searchCriteria.totalRooms = totalRooms;
          draft.roomProgression.roomsRemaining = totalRooms - draft.roomProgression.roomsAdded;
          draft.metadata.timestamp = new Date().toISOString();
        })
      );
    },

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // CART MANAGEMENT
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

    /**
     * Update cart ID after createCart API success
     * Sets cart expiration time from API response
     */
    updateCartId: ({ cartId, expirationTime }: { cartId: string; expirationTime: string }) => {
      set(
        produce((draft: MixedInventoryBookingState) => {
          draft.cartState.cartId = cartId;
          draft.cartState.expirationTime = expirationTime;
          draft.metadata.timestamp = new Date().toISOString();
        })
      );
    },

    /**
     * Updated cart extension counts
     */
    setCartExtensionCount: (count: number) => {
      set(
        produce((draft: MixedInventoryBookingState) => {
          draft.cartState.cartExtensionCount = count;
        })
      );
    },

    /**
     * Add room to cart after successful createCart or addItemToCart
     *
     * @flow
     * 1. Add room to rooms array
     * 2. Increment roomsAdded count
     * 3. Recalculate roomsRemaining
     * 4. Set addedAt timestamp
     */
    addRoomToCart: (room: SelectedRoom) => {
      set(
        produce((draft: MixedInventoryBookingState) => {
          // Add room with timestamp
          draft.cartState.rooms.push({
            ...room,
            addedAt: new Date().toISOString(),
          });

          // Update progression
          draft.roomProgression.roomsAdded += 1;
          draft.roomProgression.roomsRemaining = draft.searchCriteria.totalRooms - draft.roomProgression.roomsAdded;

          // Update metadata
          draft.metadata.timestamp = new Date().toISOString();

          // Update UI state
          draft.uiState.showReviewResLink = draft.roomProgression.roomsAdded >= 1;
        })
      );
    },

    /**
     * Remove room from cart (e.g., after removeItemFromCart API)
     * Recalculates progression counts
     */
    removeRoomFromCart: (roomIndex: number) => {
      set(
        produce((draft: MixedInventoryBookingState) => {
          const roomIndexInArray = draft.cartState.rooms.findIndex(r => r.roomIndex === roomIndex);
          if (roomIndexInArray !== -1) {
            draft.cartState.rooms.splice(roomIndexInArray, 1);
            draft.roomProgression.roomsAdded -= 1;
            draft.roomProgression.roomsRemaining = draft.searchCriteria.totalRooms - draft.roomProgression.roomsAdded;
            draft.uiState.showReviewResLink = draft.roomProgression.roomsAdded >= 1;
            draft.metadata.timestamp = new Date().toISOString();
          }
        })
      );
    },

    /**
     * Clear cart (abandon or expire)
     * Keeps search criteria and room counts
     */
    clearCart: () => {
      set(
        produce((draft: MixedInventoryBookingState) => {
          draft.cartState = {
            cartId: null,
            expirationTime: null,
            rooms: [],
            cartExtensionCount: 0,
          };
          draft.roomProgression.roomsAdded = 0;
          draft.roomProgression.roomsRemaining = draft.searchCriteria.totalRooms;
          draft.roomProgression.currentRoomIndex = 1;
          draft.uiState.showReviewResLink = false;
          draft.metadata.timestamp = new Date().toISOString();
        })
      );
    },

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // ROOM PROGRESSION MANAGEMENT
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

    /**
     * Increment room progress after successful cart operation
     * Moves from Room N to Room N+1
     */
    incrementRoomProgress: () => {
      set(
        produce((draft: MixedInventoryBookingState) => {
          draft.roomProgression.currentRoomIndex += 1;
          draft.metadata.timestamp = new Date().toISOString();
        })
      );
    },

    /**
     * Set current room index explicitly
     * Used for back navigation and checkout return
     */
    setCurrentRoomIndex: (index: number) => {
      set(
        produce((draft: MixedInventoryBookingState) => {
          draft.roomProgression.currentRoomIndex = index;
          draft.metadata.timestamp = new Date().toISOString();
        })
      );
    },

    /**
     * Navigate to next room (Room N -> Room N+1)
     * Only if not on last room
     */
    goToNextRoom: () => {
      const state = get();
      if (state.roomProgression.currentRoomIndex < state.searchCriteria.totalRooms) {
        state.incrementRoomProgress();
      }
    },

    /**
     * Navigate to previous room (Room N -> Room N-1)
     * Only if not on Room 1
     */
    goToPreviousRoom: () => {
      set(
        produce((draft: MixedInventoryBookingState) => {
          if (draft.roomProgression.currentRoomIndex > 1) {
            draft.roomProgression.currentRoomIndex -= 1;
            draft.navigation.previousRoomIndex = draft.roomProgression.currentRoomIndex;
            draft.metadata.timestamp = new Date().toISOString();
          }
        })
      );
    },

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // UI STATE MANAGEMENT
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

    setShowAbandonCartModal: (show: boolean) => {
      set(
        produce((draft: MixedInventoryBookingState) => {
          draft.uiState.showAbandonCartModal = show;
        })
      );
    },

    setShowDateChangeModal: (show: boolean) => {
      set(
        produce((draft: MixedInventoryBookingState) => {
          draft.uiState.showDateChangeModal = show;
        })
      );
    },

    setShowInventoryShortage: (show: boolean) => {
      set(
        produce((draft: MixedInventoryBookingState) => {
          draft.uiState.showInventoryShortage = show;
        })
      );
    },

    setShowRoomAddedFlyout: (show: boolean) => {
      set(
        produce((draft: MixedInventoryBookingState) => {
          draft.uiState.showRoomAddedFlyout = show;
        })
      );
    },

    /**
     * Close all modals at once
     * Used after user action or navigation
     */
    closeAllModals: () => {
      set(
        produce((draft: MixedInventoryBookingState) => {
          draft.uiState.showAbandonCartModal = false;
          draft.uiState.showDateChangeModal = false;
          draft.uiState.showInventoryShortage = false;
          draft.uiState.showRoomAddedFlyout = false;
        })
      );
    },

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // NAVIGATION MANAGEMENT
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

    setCanGoBack: (canGoBack: boolean) => {
      set(
        produce((draft: MixedInventoryBookingState) => {
          draft.navigation.canGoBack = canGoBack;
        })
      );
    },

    setReturnedFromCheckout: (returned: boolean, action?: 'back' | 'addAnother') => {
      set(
        produce((draft: MixedInventoryBookingState) => {
          draft.navigation.returnedFromCheckout = returned;
          draft.navigation.checkoutReturnAction = action || null;
        })
      );
    },

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // ERROR MANAGEMENT
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

    setCartCreationError: (error: string | null) => {
      set(
        produce((draft: MixedInventoryBookingState) => {
          draft.errors.cartCreationError = error;
        })
      );
    },

    setInventoryShortage: (shortage: boolean) => {
      set(
        produce((draft: MixedInventoryBookingState) => {
          draft.errors.inventoryShortage = shortage;
        })
      );
    },

    setDatesMismatch: (mismatch: boolean) => {
      set(
        produce((draft: MixedInventoryBookingState) => {
          draft.errors.datesMismatch = mismatch;
        })
      );
    },

    clearErrors: () => {
      set(
        produce((draft: MixedInventoryBookingState) => {
          draft.errors = {
            cartCreationError: null,
            inventoryShortage: false,
            datesMismatch: false,
          };
        })
      );
    },

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // COMPUTED / HELPER METHODS
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

    /**
     * Check if current room is the last room to select
     * @returns true if currentRoomIndex === totalRooms
     */
    isLastRoom: (): boolean => {
      const state = get();
      return state.roomProgression.currentRoomIndex === state.searchCriteria.totalRooms;
    },

    /**
     * Check if booking is for single room
     * @returns true if totalRooms === 1
     */
    isSingleRoomBooking: (): boolean => {
      const state = get();
      return state.searchCriteria.totalRooms === 1;
    },

    /**
     * Determine current Mixed Inventory scenario
     *
     * @returns
     *   1 = Single room (Rooms=1)
     *   2 = More rooms to select (not last room)
     *   3 = Last room (Room 3 of 3)
     */
    getCurrentScenario: (): MixedInventoryScenario => {
      const state = get();

      // Scenario 1: Single room booking
      if (state.isSingleRoomBooking()) {
        return 1;
      }

      // Scenario 3: Last room of multi-room booking
      if (state.isLastRoom()) {
        return 3;
      }

      // Scenario 2: Intermediate room (Room 1 of 3, Room 2 of 3)
      return 2;
    },

    /**
     * Check if cart exists
     * @returns true if cartId is not null
     */
    hasExistingCart: (): boolean => {
      const state = get();
      return state.cartState.cartId !== null;
    },

    /**
     * Get room by index
     * @param index - 1-based room index
     * @returns SelectedRoom or undefined if not found
     */
    getRoomByIndex: (index: number): SelectedRoom | undefined => {
      const state = get();
      return state.cartState.rooms.find(room => room.roomIndex === index);
    },

    /**
     * Get total rooms currently in cart
     * @returns count of rooms in cart
     */
    getTotalRoomsInCart: (): number => {
      const state = get();
      return state.cartState.rooms.length;
    },
  };
};

/**
 * Create Mixed Inventory store with session storage persistence
 *
 * @persistence Session storage with 'mixedInventoryBooking' key
 * @devtools Enabled with name 'MixedInventoryStore'
 * @skipHydration true - rehydrate explicitly on client for SSR-safe deterministic rendering
 */
export const useMixedInventoryStore = createAppStore(mixedInventoryStoreCreator, {
  usePersistentStore: true,
  persistentStoreName: 'mixedInventoryBooking',
  useLocalStorage: false, // Use session storage (expires with tab/session)
  devToolsName: 'MixedInventoryStore',
  enableDevtools: true,
  // Keep SSR output deterministic; rehydrate explicitly on the client.
  skipHydration: true,
});

/**
 * Rehydrate persisted mixed inventory state on the client.
 * Safe to call from a React useEffect in Next.js pages/components.
 */
export const rehydrateMixedInventoryStore = async (): Promise<void> => {
  if (typeof window === 'undefined') {
    return;
  }
  await useMixedInventoryStore.persist.rehydrate();
};

export const hasHydratedMixedInventoryStore = (): boolean => useMixedInventoryStore.persist.hasHydrated();

// Export types for consumer convenience
export type { MixedInventoryStore, MixedInventoryBookingState, MixedInventoryBookingActions };
export * from './types';
export * from './interface';
export * from './defaultState';
